import React from 'react';
import { AlertTriangle, Key, Database, ExternalLink } from 'lucide-react';

interface ConfigurationGuideProps {
  missingClerk?: boolean;
  missingDatabase?: boolean;
}

export function ConfigurationGuide({ missingClerk = false, missingDatabase = false }: ConfigurationGuideProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Configuración Requerida</h1>
            <p className="text-gray-600">
              Para usar esta aplicación, necesitas configurar las variables de entorno.
            </p>
          </div>

          <div className="space-y-6">
            {missingClerk && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-6">
                <div className="flex items-start">
                  <Key className="h-6 w-6 text-amber-600 mt-1 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-amber-800 mb-2">
                      Configuración de Clerk (Autenticación)
                    </h3>
                    <p className="text-amber-700 mb-4">
                      Las claves de Clerk no están configuradas o son inválidas.
                    </p>
                    <div className="bg-white rounded border border-amber-200 p-4 mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Pasos para configurar:</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                        <li>Ve a <a href="https://dashboard.clerk.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center">Clerk Dashboard <ExternalLink className="h-3 w-3 ml-1" /></a></li>
                        <li>Crea una nueva aplicación o selecciona una existente</li>
                        <li>Ve a "API Keys" en el panel lateral</li>
                        <li>Copia la "Publishable key" y la "Secret key"</li>
                        <li>Actualiza tu archivo <code className="bg-gray-100 px-1 rounded">.env.local</code></li>
                      </ol>
                    </div>
                    <div className="bg-gray-900 text-gray-100 p-4 rounded text-sm font-mono">
                      <div className="text-green-400 mb-1"># En tu archivo .env.local</div>
                      <div>VITE_CLERK_PUBLISHABLE_KEY=pk_test_tu_clave_aqui</div>
                      <div>CLERK_SECRET_KEY=sk_test_tu_clave_secreta_aqui</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {missingDatabase && (
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-6">
                <div className="flex items-start">
                  <Database className="h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">
                      Configuración de Neon Database
                    </h3>
                    <p className="text-blue-700 mb-4">
                      La URL de la base de datos no está configurada correctamente.
                    </p>
                    <div className="bg-white rounded border border-blue-200 p-4 mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Pasos para configurar:</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                        <li>Ve a <a href="https://console.neon.tech/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center">Neon Console <ExternalLink className="h-3 w-3 ml-1" /></a></li>
                        <li>Crea un nuevo proyecto o selecciona uno existente</li>
                        <li>Ve a "Connection Details" o "Dashboard"</li>
                        <li>Copia la "Connection string" completa</li>
                        <li>Asegúrate de que incluya <code className="bg-gray-100 px-1 rounded">?sslmode=require</code></li>
                      </ol>
                    </div>
                    <div className="bg-gray-900 text-gray-100 p-4 rounded text-sm font-mono">
                      <div className="text-green-400 mb-1"># En tu archivo .env.local</div>
                      <div className="break-all">DATABASE_URL=postgresql://usuario:password@ep-xxx.neon.tech/neondb?sslmode=require</div>
                      <div className="break-all">VITE_DATABASE_URL=postgresql://usuario:password@ep-xxx.neon.tech/neondb?sslmode=require</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border border-gray-200 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Después de configurar:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                <li>Guarda el archivo <code className="bg-gray-100 px-1 rounded">.env.local</code></li>
                <li>Reinicia el servidor de desarrollo:</li>
              </ol>
              <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono mt-3">
                <div className="text-green-400 mb-1"># Detén el servidor (Ctrl+C) y ejecuta:</div>
                <div>npm run dev</div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Documentación adicional</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Para instrucciones más detalladas, consulta:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li><code className="bg-blue-100 px-1 rounded">SETUP.md</code> - Guía completa de configuración</li>
                      <li><code className="bg-blue-100 px-1 rounded">CONFIGURACION.md</code> - Solución de problemas</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfigurationGuide;